/**
 * Toolbar view, search, filters, columns, and export progress for /schedule/tasks.
 * Does not own the DHTMLX grid, save, or schedule fetch.
 */
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { ReactGanttRef } from '@dhx/react-gantt';
import { useDebouncedValue } from '@mantine/hooks';
import type { SetURLSearchParams } from 'react-router-dom';
import type { GanttLoaderMark } from '../components/GanttBusyOverlay';
import type { ZoomLevel } from '../components/ZoomControl';
import type { GanttViewMode, LookAheadPeriod } from '../components/targets';
import type { ColumnFilterValues } from '../../../gantt/columns';
import {
  loadGanttVisibleOptionalColumns,
  saveGanttVisibleOptionalColumns,
} from '../../../gantt/ganttVisibleOptionalColumns';
import {
  loadShowGanttChartPreference,
  saveShowGanttChartPreference,
} from '../../../gantt/ganttShowChartPreference';
import { ENABLE_TARGETS_QUANTITY_PANEL } from '../ganttPageFlags';

export function useGanttChrome(params: {
  projectId: string;
  ganttRef: MutableRefObject<ReactGanttRef | null>;
  featureTips: { maybeShowTip: (id: string) => void } | null | undefined;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  parentIdsWithChildren: Set<string>;
  targetsGateRef: MutableRefObject<{ dirtyCount: number; discardDrafts: () => void }>;
}) {
  const {
    projectId,
    ganttRef,
    featureTips,
    searchParams,
    setSearchParams,
    parentIdsWithChildren,
    targetsGateRef,
  } = params;

  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('month');
  const [viewMode, setViewMode] = useState<GanttViewMode>('schedule');
  /** Look-ahead filter horizon. Reset to weeks each time the user opens Look ahead. */
  const [lookAheadPeriod, setLookAheadPeriod] = useState<LookAheadPeriod>('week');
  /**
   * Targets default: look-ahead leaves + ancestors only (display filter).
   * Full WBS stays loaded in DHTMLX — we never remove tasks (avoids reparent bugs).
   */
  const [targetsShowFullWbs, setTargetsShowFullWbs] = useState(false);
  /** Tracks look-ahead window key so we auto-expand focus paths once per window. */
  const targetsFocusExpandKeyRef = useRef('');
  /** Brief overlay while Schedule↔Targets or Scale (day/week/month/year) layout work runs. */
  const [viewSwitchBusy, setViewSwitchBusy] = useState(false);
  const [viewSwitchBusyLabel, setViewSwitchBusyLabel] = useState('Updating view…');
  const [viewSwitchMark, setViewSwitchMark] = useState<GanttLoaderMark | null>(null);
  /** Display filter: show only critical-path tasks + ancestors (Schedule / Look ahead). */
  const [showCriticalPathOnly, setShowCriticalPathOnly] = useState(false);
  /** Tint critical tasks with the strip color. Does not hide other tasks. */
  const [highlightCriticalPath, setHighlightCriticalPath] = useState(false);
  /** Bump after auto-schedule / parse so critical filter rebuilds from live isCriticalTask. */
  const [criticalFilterEpoch, setCriticalFilterEpoch] = useState(0);
  const criticalFocusExpandKeyRef = useRef('');
  /** Task name search (toolbar). Debounced value drives the display filter. */
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [debouncedTaskSearch] = useDebouncedValue(taskSearchQuery, 180);
  /** Multi-column grid filters (React filter bar). */
  const [columnFilterValues, setColumnFilterValues] = useState<ColumnFilterValues>({});
  const [debouncedColumnFilterValues] = useDebouncedValue(columnFilterValues, 180);
  const columnFilterExpandKeyRef = useRef('');
  const taskSearchInputRef = useRef<HTMLInputElement>(null);
  const searchFocusExpandKeyRef = useRef('');
  const [taskSearchExpanded, setTaskSearchExpanded] = useState(false);
  const [showGanttChart, setShowGanttChart] = useState(loadShowGanttChartPreference);
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [visibleOptionalColumns, setVisibleOptionalColumns] = useState(() =>
    loadGanttVisibleOptionalColumns(projectId),
  );
  /** Skip one session write after loading prefs for a project switch. */
  const skipVisibleColumnsSessionSaveRef = useRef(false);
  const [collapsedBranchIds, setCollapsedBranchIds] = useState<Set<string>>(() => new Set());
  const collapsedBranchIdsRef = useRef(collapsedBranchIds);
  collapsedBranchIdsRef.current = collapsedBranchIds;
  /** Pending Targets discard confirmation (toolbar, leave view, or scale change). */
  const [targetsDiscardPending, setTargetsDiscardPending] = useState<
    | { kind: 'drafts' }
    | { kind: 'view'; next: GanttViewMode }
    | { kind: 'zoom'; next: ZoomLevel }
    | null
  >(null);

  useEffect(() => {
    if (!projectId) return;
    skipVisibleColumnsSessionSaveRef.current = true;
    setVisibleOptionalColumns(loadGanttVisibleOptionalColumns(projectId));
  }, [projectId]);

  /** Keep optional grid column visibility for this tab session (per project). */
  useEffect(() => {
    if (!projectId) return;
    if (skipVisibleColumnsSessionSaveRef.current) {
      skipVisibleColumnsSessionSaveRef.current = false;
      return;
    }
    saveGanttVisibleOptionalColumns(projectId, visibleOptionalColumns);
  }, [projectId, visibleOptionalColumns]);

  const applyViewModeChange = useCallback((next: GanttViewMode) => {
    // Paint busy overlay first, then flip mode so the UI doesn't look frozen.
    setViewSwitchBusy(true);
    setViewSwitchBusyLabel(next === 'targets' ? 'Opening Look ahead…' : 'Opening Schedule…');
    setViewSwitchMark(next === 'targets' ? 'look-ahead' : null);
    requestAnimationFrame(() => {
      setViewMode(next);
      if (next === 'targets') {
        setLookAheadPeriod('week');
        setTargetsShowFullWbs(false);
        targetsFocusExpandKeyRef.current = '';
      }
      // Do NOT clearAll+parse on leave Targets — look-ahead uses display filter only,
      // and reparsing ~15k tasks was the main multi-second cost of the toggle.
    });
  }, []);

  /** First Schedule / Look ahead visit: surface the matching tip if not dismissed yet. */
  useEffect(() => {
    if (viewMode === 'targets') {
      featureTips?.maybeShowTip('look-ahead');
      return;
    }
    if (viewMode === 'schedule') {
      featureTips?.maybeShowTip('schedule-view');
    }
  }, [viewMode, featureTips]);

  /** Blank-start deep link from Project setup chooser. */
  useEffect(() => {
    if (searchParams.get('started') !== 'blank') return;
    featureTips?.maybeShowTip('locations-and-tasks');
    const next = new URLSearchParams(searchParams);
    next.delete('started');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, featureTips]);

  const applyZoomLevelChange = useCallback((level: ZoomLevel) => {
    const scaleLabel =
      level === 'day' ? 'Day' : level === 'week' ? 'Week' : level === 'month' ? 'Month' : 'Year';
    setViewSwitchBusy(true);
    setViewSwitchBusyLabel(`Switching to ${scaleLabel} scale…`);
    setViewSwitchMark('zoom');
    requestAnimationFrame(() => {
      setZoomLevel(level);
    });
  }, []);

  const handleViewModeChange = useCallback(
    (next: GanttViewMode) => {
      if (next === viewMode) return;
      if (
        ENABLE_TARGETS_QUANTITY_PANEL &&
        viewMode === 'targets' &&
        targetsGateRef.current.dirtyCount > 0
      ) {
        setTargetsDiscardPending({ kind: 'view', next });
        return;
      }
      applyViewModeChange(next);
    },
    [viewMode, targetsGateRef, applyViewModeChange],
  );

  const handleZoomLevelChange = useCallback(
    (level: ZoomLevel) => {
      if (level === zoomLevel) return;
      if (
        ENABLE_TARGETS_QUANTITY_PANEL &&
        viewMode === 'targets' &&
        targetsGateRef.current.dirtyCount > 0
      ) {
        setTargetsDiscardPending({ kind: 'zoom', next: level });
        return;
      }
      applyZoomLevelChange(level);
    },
    [zoomLevel, viewMode, targetsGateRef, applyZoomLevelChange],
  );

  const handleTargetsDiscardCancel = useCallback(() => {
    setTargetsDiscardPending(null);
  }, []);

  const handleTargetsDiscardConfirm = useCallback(() => {
    if (!targetsDiscardPending) return;
    const pending = targetsDiscardPending;
    setTargetsDiscardPending(null);
    targetsGateRef.current.discardDrafts();
    if (pending.kind === 'view') {
      applyViewModeChange(pending.next);
    } else if (pending.kind === 'zoom') {
      applyZoomLevelChange(pending.next);
    }
  }, [targetsDiscardPending, targetsGateRef, applyViewModeChange, applyZoomLevelChange]);

  /** DHTMLX ignores `open: false` on task updates; must set `$open` + render. */
  const applyTreeOpenState = useCallback((expanded: boolean) => {
    const gantt = ganttRef.current?.instance as {
      eachTask?: (cb: (task: { id: string | number; $open?: boolean }) => void) => void;
      hasChild?: (id: string | number) => boolean;
      batchUpdate?: (cb: () => void) => void;
      render?: () => void;
    } | undefined;
    if (!gantt?.eachTask) return;

    const apply = () => {
      gantt.eachTask?.((task) => {
        if (gantt.hasChild?.(task.id)) {
          task.$open = expanded;
        }
      });
    };

    if (typeof gantt.batchUpdate === 'function') {
      gantt.batchUpdate(apply);
    } else {
      apply();
      gantt.render?.();
    }
  }, [ganttRef]);

  const handleExpandAllToggle = useCallback(
    (expanded: boolean) => {
      setCollapsedBranchIds(expanded ? new Set() : new Set(parentIdsWithChildren));
      applyTreeOpenState(expanded);
    },
    [parentIdsWithChildren, applyTreeOpenState],
  );

  const allTasksExpanded = collapsedBranchIds.size === 0;

  const toggleShowGanttChart = useCallback(() => {
    setShowGanttChart((current) => {
      const next = !current;
      saveShowGanttChartPreference(next);
      return next;
    });
  }, []);

  return {
    zoomLevel,
    setZoomLevel,
    viewMode,
    setViewMode,
    lookAheadPeriod,
    setLookAheadPeriod,
    targetsShowFullWbs,
    setTargetsShowFullWbs,
    targetsFocusExpandKeyRef,
    viewSwitchBusy,
    setViewSwitchBusy,
    viewSwitchBusyLabel,
    viewSwitchMark,
    showCriticalPathOnly,
    setShowCriticalPathOnly,
    highlightCriticalPath,
    setHighlightCriticalPath,
    criticalFilterEpoch,
    setCriticalFilterEpoch,
    criticalFocusExpandKeyRef,
    taskSearchQuery,
    setTaskSearchQuery,
    debouncedTaskSearch,
    columnFilterValues,
    setColumnFilterValues,
    debouncedColumnFilterValues,
    columnFilterExpandKeyRef,
    taskSearchInputRef,
    searchFocusExpandKeyRef,
    taskSearchExpanded,
    setTaskSearchExpanded,
    showGanttChart,
    setShowGanttChart,
    toggleShowGanttChart,
    keyboardShortcutsOpen,
    setKeyboardShortcutsOpen,
    exporting,
    setExporting,
    exportingExcel,
    setExportingExcel,
    exportProgress,
    setExportProgress,
    visibleOptionalColumns,
    setVisibleOptionalColumns,
    collapsedBranchIds,
    setCollapsedBranchIds,
    collapsedBranchIdsRef,
    targetsDiscardPending,
    setTargetsDiscardPending,
    applyViewModeChange,
    applyZoomLevelChange,
    handleViewModeChange,
    handleZoomLevelChange,
    handleTargetsDiscardCancel,
    handleTargetsDiscardConfirm,
    applyTreeOpenState,
    handleExpandAllToggle,
    allTasksExpanded,
  };
}
